import {get, isArray, isEmpty} from 'lodash';

import {HierarchyNodes} from './hierarchy';
import {SearchResultItem} from './search_result_item';

const FILE_PROJECT_PROPERTIES = 'properties';
const FILE_PROJECT_PROPERTY_TYPE_NAME = 'typeName';
const FILE_PROJECT_PROPERTY_VALUE = 'value';
const FILE_BLOB = 'blob';
const FILE_BLOB_ID = 'id';
const FILE_FILE_NAME = 'filename';
const FILE_HIERARCHIES = 'simpleHierarchies';
const FILE_ID = 'id';
const FILE_LINKED_FILES = 'linkedFiles';
const FILE_OTHER_FORMATS = 'otherFormats';
const FILE_SRC_FILE = 'srcFile';
const FILE_SUBTITLE = 'subtitle';
const FILE_TYPE = 'fileType';
const FILE_TEXT_EXCERPT = 'textExcerpt';

const LINKED_FILE_FILE = 'file';

const STATIC_EMPTY_OBJECT = {};

/* eslint-disable no-use-before-define */
export class LinkedFile {
  constructor(rawData = {}) {
    this.rawData = {...rawData};
    this.file = new File(this.rawData[LINKED_FILE_FILE]);
  }

  getFile() {
    return this.file;
  }

  getRawData() {
    return this.rawData;
  }

  getTableProjection(config) {
    return {
      ...this.getFile().getTableProjection(config),
      ...this.getRawData(),
    };
  }
}

/* eslint-enable no-use-before-define */

export class File extends SearchResultItem {
  static createDownloadUrl(config, blobId) {
    return `${config.service.url.files}/${config.service.url.fileDownloadMethod}/${blobId}`;
  }

  static getPropertyLabel(propertyName, labels) {
    return labels && labels[propertyName] ? labels[propertyName] : propertyName;
  }

  static initHierarchies(rawDataHierarchies) {
    const hierarchies = [];
    if (rawDataHierarchies) {
      rawDataHierarchies.forEach((hierarchy) => {
        hierarchies.push(new HierarchyNodes(hierarchy));
      });
    }
    return hierarchies;
  }

  static initLinkedFiles(rawDataLinks) {
    const links = [];
    if (rawDataLinks) {
      if (rawDataLinks) {
        rawDataLinks.forEach((link) => {
          links.push(new LinkedFile(link));
        });
      }
    }
    return links;
  }

  static initOtherFormats(rawDataOtherFormats) {
    const otherFormats = [];
    if (rawDataOtherFormats) {
      rawDataOtherFormats.forEach((otherFormat) => {
        otherFormats.push(new File(otherFormat));
      });
    }
    return otherFormats;
  }

  static initProjectPropertiesMap(rawDataProjectProperties) {
    const projectProperties = {};
    if (rawDataProjectProperties && isArray(rawDataProjectProperties)) {
      rawDataProjectProperties.forEach((property) => {
        projectProperties[property[FILE_PROJECT_PROPERTY_TYPE_NAME]] =
          property[FILE_PROJECT_PROPERTY_VALUE];
      });
    }
    return projectProperties;
  }

  static filterTextExcerpt(text = '') {
    return text.replace(/###IMAGE###/g, '');
  }

  constructor(rawData = {}) {
    super();
    this.rawData = {...rawData};
    this.hierarchies = File.initHierarchies(this.rawData[FILE_HIERARCHIES]);
    this.linkedFiles = File.initLinkedFiles(this.rawData[FILE_LINKED_FILES]);
    this.otherFormats = File.initOtherFormats(this.rawData[FILE_OTHER_FORMATS]);
    this.projectProperties = File.initProjectPropertiesMap(
      this.rawData[FILE_PROJECT_PROPERTIES]
    );
  }

  getId() {
    return this.rawData[FILE_ID];
  }

  getRawData() {
    return this.rawData;
  }

  getTableProjection(config, metadata = {}) {
    return {
      ...this.getRawData(),
      ...this.getDownloadDataProjection(config),
      ...metadata,
    };
  }

  getDownloadDataProjection(config) {
    return {
      downloadData: {
        config,
        blobId: this.getBlobId(),
      },
    };
  }

  getBlobId() {
    return (
      (this.rawData[FILE_BLOB]
        ? this.rawData[FILE_BLOB][FILE_BLOB_ID]
        : undefined) ||
      (this.getSrcFile()[FILE_BLOB]
        ? this.getSrcFile()[FILE_BLOB][FILE_BLOB_ID]
        : undefined)
    );
  }

  getProjectProperties() {
    return this.projectProperties;
  }

  getProjectProperty(propertyName) {
    let value;
    if (propertyName.indexOf('.') !== -1) {
      value = get(this.getProjectProperties(), propertyName);
    } else {
      value = this.getProjectProperties()[propertyName];
    }
    return value;
  }

  getDocumentTitle() {
    return this.getFileName();
  }

  getDocumentId() {
    return this.rawData[FILE_ID] || this.getSrcFile()[FILE_ID];
  }

  getFileName() {
    return this.rawData[FILE_FILE_NAME] || this.getSrcFile()[FILE_FILE_NAME];
  }

  getName() {
    return this.getFileName();
  }

  getSubTitle() {
    return this.getProjectProperty(FILE_SUBTITLE);
  }

  getTextSnippets() {
    let result = [];
    if (isArray(this.rawData[FILE_TEXT_EXCERPT])) {
      result = this.rawData[FILE_TEXT_EXCERPT].map((item) =>
        File.filterTextExcerpt(item)
      );
    } else if (!isEmpty(this.rawData[FILE_TEXT_EXCERPT])) {
      result = [File.filterTextExcerpt(this.rawData[FILE_TEXT_EXCERPT])];
    }
    return result;
  }

  getHierarchies() {
    return this.hierarchies;
  }

  getLinkedFiles() {
    return this.linkedFiles;
  }

  getOtherFormats() {
    return this.otherFormats;
  }

  getSrcFile() {
    return this.rawData[FILE_SRC_FILE] || STATIC_EMPTY_OBJECT;
  }

  getType() {
    return this.rawData[FILE_TYPE] || this.getSrcFile()[FILE_TYPE];
  }

  getProperty(propertyName) {
    let value;
    if (propertyName.indexOf('.') !== -1) {
      value = get(this.rawData, propertyName);
    } else {
      value = this.rawData[propertyName] || this.getSrcFile()[propertyName];
    }
    return value;
  }
}

export default {File};
